function debounce<T extends unknown[]>(func: (...args: T) => void, execAsap?: boolean): (...args: T) => void {
    let timeout: number | null;

    return function debounced<This>(this: This, ...args: T) {
        const delayed = () => {
            if (!execAsap) {
                func.apply(this, args);
            }

            timeout = null;
        };

        if (timeout) {
            cancelAnimationFrame(timeout);
        } else if (execAsap) {
            func.apply(this, args);
        }

        timeout = requestAnimationFrame(delayed);
    };
}

function debouncePromise<T extends unknown[], R extends unknown>(func: (...args: T) => R, delay = 1000):
    (...args: T) => Promise<R> {
    let timer: number | undefined;
    let lastResolve: ((value: R) => void) | null = null;

    return function (...args: T) {
        window.clearTimeout(timer);
        timer = window.setTimeout(() => {
            const result = func(...args);
            lastResolve!(result);
            lastResolve = null;
        }, delay);

        return new Promise(r => {
            lastResolve = r;
        });
    };
}

class DebouncerWithCooldown<T extends unknown[], R> {
    // Inputs
    private target: (...args: T) => R;
    private delay: number;
    // State
    private timeout: number | undefined;
    private timeoutFn: null | (() => void) = null;
    private lastCall = Date.now();
    private lastResolve: ((value: R) => void) | null = null;
    private lastPromise: Promise<R> | null = null;
    private chainedCallbacks: Record<string, () => void> = {};

    constructor(target: (...args: T) => R, delay = 1000) {
        this.target = target;
        this.delay = delay;
    }

    private reset = () => {
        this.timeout = undefined;
        this.timeoutFn = null;
        this.lastCall = Date.now();
        this.lastResolve = null;
        this.lastPromise = null;
    };

    // Execute the function in timeout immedieately.
    public executeImmediately = () => {
        if (this.timeout) {
            this.timeoutFn?.();
            clearTimeout(this.timeout);
        }
    };

    public trigger = (...args: T): Promise<R> => {
        // If no pending debounced calls and the last call was more than `delay` ms ago, execute immediately
        if (!this.timeout && Date.now() - this.lastCall >= this.delay) {
            const result = this.target(...args);
            this.lastCall = Date.now();
            return Promise.resolve(result);
        }

        // Otherwise, wait for `delay` ms before executing
        this.timeoutFn = () => {
            // Execute the callback and resolve the last promise
            const result = this.target(...args);
            // `target` function may call `abort` inside,
            // therefore `lastResolve` might be null at this point
            this.lastResolve?.(result);
            // Reset state
            this.reset();
        };

        window.clearTimeout(this.timeout);
        this.timeout = window.setTimeout(this.timeoutFn, this.delay);

        this.lastPromise = new Promise(r => {
            this.lastResolve = r;
        });
        return this.lastPromise;
    };

    public chain = (key: string, callback: () => void) => {
        if (this.lastPromise) {
            this.chainedCallbacks[key] = callback;
            this.lastPromise.then(() => {
                callback();
                delete this.chainedCallbacks[key];
            });
        } else {
            callback();
        }
    };

    public abort = () => {
        // Abort current pending debounced call
        window.clearTimeout(this.timeout);
        // Reset state
        this.reset();
        // Execute any other chained callbacks
        Object.entries(this.chainedCallbacks).forEach(([key, callback]) => {
            callback();
            delete this.chainedCallbacks[key];
        });
    };
}

function debounceWithTimeout<T extends unknown[]>(func: (...args: T) => void, timeoutMS: number): (...args: T) => void {
    let timeoutId: number | undefined;

    return function debounced<This>(this: This, ...args: T) {
        if (timeoutId) {
            window.clearTimeout(timeoutId);
        }

        timeoutId = window.setTimeout(() => func.apply(this, args), timeoutMS);
    };
}

export default debounce;
export { debouncePromise, DebouncerWithCooldown, debounceWithTimeout };

